import React, { useState, useEffect, createContext, useContext } from 'react';
import { Toaster as SonnerToaster } from 'sonner';
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import StudentRegistrationForm from './components/StudentRegistrationForm';
import { LanguageProvider, LanguageToggle, useLanguage } from './contexts/LanguageContext';
import PublicFeatureDetailPage from './pages/PublicFeatureDetailPage';
import PublicFeaturesListPage from './pages/PublicFeaturesListPage';
import FeatureMarketingPage from './pages/FeatureMarketingPage';
import FeatureGatePage from './components/FeatureGatePage';
import UDISEAffiliationPage from './pages/UDISEAffiliationPage';
import SocialMediaPage from './pages/SocialMediaPage';
import NewsletterManager from './pages/NewsletterManager';
import DataTasksPage from './pages/DataTasksPage';
import SchoolFinderPage from './pages/SchoolFinderPage';
import MySchoolProfilePage from './pages/MySchoolProfilePage';
import DataSourcesPage from './pages/DataSourcesPage';
import ChatResumePage from './pages/ChatResumePage';
import AayushRulebookEditor from './pages/AayushRulebookEditor';
import AayushKnowledgeBase from './pages/AayushKnowledgeBase';
import LandingPage from './pages/LandingPage';
import FoundingOfferPage from './pages/FoundingOfferPage';
import LeadCRM from './components/LeadCRM';
import FeatureEditor from './components/FeatureEditor';
import LoginPage from './pages/LoginPage';
import FirstTimeSetupPage from './pages/FirstTimeSetupPage';
import PlatformDashboard from './pages/PlatformDashboard';
import AIFeaturesPage from './pages/AIFeaturesPage';
import SettingsPage from './pages/SettingsPage';
import StudentsPage from './pages/StudentsPage';
import ParentDashboardPage from './pages/ParentDashboardPage';
import BulkImportPage from './pages/BulkImportPage';
import WalletPage from './pages/WalletPage';
import TodayAIUsagePage from './pages/TodayAIUsagePage';
import SchoolDashboard from './pages/SchoolDashboardPage';
import SchoolProfile360 from './components/SchoolProfile360';
import OnboardingScheduler from './pages/OnboardingScheduler';
import BillingMyPage from './pages/BillingMyPage';
import ClassesPage from './pages/ClassesPage';
import { TeachersPage, FeesPage, CommunicationPage, ComplaintsPage } from './pages/SchoolModulesPages';
import AttendanceFullPage from './pages/AttendanceFullPage';
import ExamsResultsPage from './pages/ExamsResultsPage';
import { WhatsAppPage, ReportsPage, LeaveManagementPage, LeaveApplyPage, EmergencyExitPage } from './pages/AdminModulesPages';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import FollowUpsPage from './pages/FollowUpsPage';
import AdmissionEngineDashboard from './pages/AdmissionEngineDashboard';
import SchoolMatchPage from './pages/SchoolMatchPage';
import PadhaiEMIPage from './pages/PadhaiEMIPage';
import FeeStructurePage from './pages/FeeStructurePage';
import DikshaChat from './pages/DikshaChat';
import PublicEntranceExam from './pages/PublicEntranceExam';
import ParentAdmissionPortal from './pages/ParentAdmissionPortal';
import SetupGuidePage from './pages/SetupGuidePage';
import SchoolAnalyticsPage from './pages/SchoolAnalyticsPage';
import PaymentRedirectPage from './pages/PaymentRedirectPage';
import ActivatePage from './pages/ActivatePage';
import BookDemoPage from './pages/BookDemoPage';
import CampaignManager from './pages/CampaignManager';
import CalendarPageFull from './pages/CalendarPageFull';
import LeaveRequestsPageFull from './pages/LeaveRequestsPageFull';
import { 
  ParentPortalDashboard,
} from './pages/PlaceholderPages';
import {
  PortalAttendancePage,
  PortalFeesPage,
  PortalAnnouncementsPage,
  PortalComplaintsPage,
} from './pages/PortalPages';
import TimetableFullPage from './pages/TimetableFullPage';
import TransportFullPage from './pages/TransportFullPage';
import PortalBusTrackingPage from './pages/PortalBusTrackingPage';
import HRPayrollPage from './pages/HRPayrollPage';
import LibraryPage from './pages/LibraryPage';
import DomainSettingsPage from './pages/DomainSettingsPage';
import NBFCApprovalManager from './components/admin/NBFCApprovalManager';
import ReceiptPage from './pages/ReceiptPage';
import PayNowPage from './pages/PayNowPage';
import PricingPage from './pages/PricingPage';
import PayNowStartPage from './pages/PayNowStartPage';
import NotificationCenter from './pages/NotificationCenter';
import AuditLogsPage from './pages/AuditLogsPage';
import DoubtHistoryPage from './pages/DoubtHistoryPage';
import SchedulerPage from './pages/SchedulerPage';
import DocumentsPage from './pages/DocumentsPage';
import CompetitionComparisonPage from './pages/CompetitionComparisonPage';
import DataExportPage from './pages/DataExportPage';
import LeadNurturingPage from './pages/LeadNurturingPage';
import { PrivacyPolicy, TermsConditions, RefundPolicy, ContactUs, AboutUs } from './pages/LegalPages';

// Import Layout Components
import { Header, Sidebar, BackBar, BreadcrumbProvider } from './components/layout';
import { DemoModeProvider, DemoModeBanner, DemoFloatingActivate, DemoActivateModal, AccountSuspendedGate } from './components/demo/DemoMode';
import WelcomeTour from './components/demo/WelcomeTour';
import SchoolNavTour from './components/onboarding/SchoolNavTour';
import LiveChat from './components/landing-page/LiveChat';
import DikshaGuidedDemo from './components/demo/DikshaGuidedDemo';
import LanguagePromptModal from './components/LanguagePromptModal';

// Import Shared Components
import { Modal, Toast, StatCard } from './components/shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Context
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// ---------- Global demo write-block gate ----------
// Any 402 {detail.demo_restricted} response (from ANY component — fetch, api helper,
// uploads) opens the "Activate Trial" agreement modal. When the user agrees and the
// trial is activated, the ORIGINAL request is transparently retried so their data is
// saved with no re-typing.
let _demoGateResolvers = [];
const _openDemoGate = (detail) => new Promise((resolve) => {
  _demoGateResolvers.push(resolve);
  window.dispatchEvent(new CustomEvent('demo:write-blocked', { detail }));
});
if (typeof window !== 'undefined' && !window.__demoFetchPatched) {
  window.__demoFetchPatched = true;
  const _origFetch = window.fetch.bind(window);
  window.addEventListener('demo:activated', () => { _demoGateResolvers.splice(0).forEach((r) => r(true)); });
  window.addEventListener('demo:activate-cancelled', () => { _demoGateResolvers.splice(0).forEach((r) => r(false)); });
  window.fetch = async (input, init) => {
    const res = await _origFetch(input, init);
    if (res.status !== 402) return res;
    let detail = null;
    try { detail = (await res.clone().json())?.detail; } catch { /* not json */ }
    if (!detail || !detail.demo_restricted) return res;
    const activated = await _openDemoGate(detail);
    if (!activated) return res;            // user cancelled -> surface original 402
    return await _origFetch(input, init);  // account is now trial -> retry & save
  };
}

const _safeJson = async (res) => {
  try { return await res.json(); } catch { return null; }
};

const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await _safeJson(res);
      if (!res.ok) {
        return { success: false, error: data?.detail || `HTTP ${res.status}`, status: res.status };
      }
      return data ?? { success: false, error: 'Empty response' };
    } catch (err) {
      console.error('API GET error:', err);
      return { success: false, error: err.message };
    }
  },
  post: async (endpoint, payload, token = null) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      const data = await _safeJson(res);
      if (!res.ok) {
        return { success: false, error: data?.detail || `HTTP ${res.status}`, status: res.status };
      }
      return data ?? { success: false, error: 'Empty response' };
    } catch (err) {
      console.error('API POST error:', err);
      return { success: false, error: err.message };
    }
  },
  put: async (endpoint, payload, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await _safeJson(res);
      if (!res.ok) {
        return { success: false, error: data?.detail || `HTTP ${res.status}`, status: res.status };
      }
      return data ?? { success: false, error: 'Empty response' };
    } catch (err) {
      console.error('API PUT error:', err);
      return { success: false, error: err.message };
    }
  },
  delete: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await _safeJson(res);
      if (!res.ok) {
        _maybeFireDemoBlock(res.status, data);
        return { success: false, error: data?.detail || `HTTP ${res.status}`, status: res.status };
      }
      return data ?? { success: false, error: 'Empty response' };
    } catch (err) {
      console.error('API DELETE error:', err);
      return { success: false, error: err.message };
    }
  }
};


// Platform Dashboard
function AddSchoolModal({ isOpen, onClose, token, onSuccess }) {
  const [formData, setFormData] = useState({ name: '', subdomain: '', board: 'MP Board', phone: '', email: '', principal_name: '', city: '', district: '', state: 'Madhya Pradesh', pincode: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await api.post('/api/schools/', {
      name: formData.name,
      subdomain: formData.subdomain,
      board: formData.board,
      phone: formData.phone,
      email: formData.email,
      principal_name: formData.principal_name,
      address: { line1: formData.city, city: formData.city, district: formData.district, state: formData.state, pincode: formData.pincode }
    }, token);
    setLoading(false);
    if (res.success) { onSuccess(); onClose(); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New School">
      <form onSubmit={handleSubmit} className="space-y-4">
        <input placeholder="School Name *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required />
        <input placeholder="Subdomain *" value={formData.subdomain} onChange={(e) => setFormData({...formData, subdomain: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required />
        <select value={formData.board} onChange={(e) => setFormData({...formData, board: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
          <option value="MP Board">MP Board</option>
          <option value="CBSE">CBSE</option>
          <option value="ICSE">ICSE</option>
        </select>
        <input placeholder="Phone *" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required />
        <input placeholder="Email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
        <input placeholder="Principal Name" value={formData.principal_name} onChange={(e) => setFormData({...formData, principal_name: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
        <div className="grid grid-cols-2 gap-4">
          <input placeholder="City *" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required />
          <input placeholder="District *" value={formData.district} onChange={(e) => setFormData({...formData, district: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required />
        </div>
        <input placeholder="Pincode *" value={formData.pincode} onChange={(e) => setFormData({...formData, pincode: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required />
        <button type="submit" disabled={loading} className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50">
          {loading ? 'Creating...' : 'Create School'}
        </button>
      </form>
    </Modal>
  );
}

// School Dashboard - Inline version (keeping for reference, using imported version)

// Classes Page - Inline version (keeping for reference, using imported version)

function AddClassForm({ onSubmit }) {
  const [formData, setFormData] = useState({ name: '', grade: '', academic_year: '2025-26' });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-4">
      <select value={formData.name} onChange={(e) => { const g = e.target.selectedIndex; setFormData({...formData, name: e.target.value, grade: g}); }} className="w-full px-4 py-2 border rounded-lg" required>
        <option value="">Select Class</option>
        <option value="Nursery">Nursery</option>
        <option value="LKG">LKG</option>
        <option value="UKG">UKG</option>
        {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => <option key={n} value={`Class ${n}`}>Class {n}</option>)}
      </select>
      <input placeholder="Academic Year" value={formData.academic_year} onChange={(e) => setFormData({...formData, academic_year: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
      <button type="submit" className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600">Create Class</button>
    </form>
  );
}

function AddSectionForm({ classId, onSubmit }) {
  const [formData, setFormData] = useState({ name: 'A', capacity: 40 });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...formData, class_id: classId }); }} className="space-y-4">
      <select value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
        {['A','B','C','D','E'].map(s => <option key={s} value={s}>Section {s}</option>)}
      </select>
      <input type="number" placeholder="Capacity" value={formData.capacity} onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})} className="w-full px-4 py-2 border rounded-lg" />
      <button type="submit" className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600">Create Section</button>
    </form>
  );
}

// Students Page


// Attendance Page

// Fees Page



// Communication Page


// Complaints Page


// Parent Dashboard Page - Unified view for parents

// Protected Route Wrapper
function ProtectedRoute({ children, loginRedirect }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!user) {
    // Most protected pages show a feature explanation page when logged-out.
    // Pages flagged loginRedirect (e.g. /onboarding-schedule deep-linked from WhatsApp)
    // send the user to login and bring them right back after they sign in.
    if (loginRedirect) {
      return <Navigate to="/login" state={{ from: { pathname: location.pathname + location.search } }} replace />;
    }
    return <FeatureGatePage />;
  }

  // First-login forced flow: redirect to /first-time-setup until both flags clear.
  // Skipping this redirect for the setup page itself prevents an infinite loop.
  // Conservative: only force the wizard for newly-provisioned school admins (needs_initial_setup === true).
  // Existing school_admins without this flag are NOT affected.
  const needsSetup = user.temp_password_required || (user.role === 'school_admin' && user.needs_initial_setup === true);
  if (needsSetup && location.pathname !== '/first-time-setup') {
    return <Navigate to="/first-time-setup" replace />;
  }

  return children;
}

// Dashboard Layout with Header and Sidebar
// Map current route -> Aayush page context {label, tip} for context-aware help
const AAYUSH_PAGE_MAP = {
  dashboard: { label: 'Dashboard', tip: 'आज का overview यहाँ है। किसी feature की मदद चाहिए?' },
  students: { label: 'Students', tip: 'CSV से 100+ students एक साथ import करें — दिखाऊँ कैसे?' },
  teachers: { label: 'Teachers / Staff', tip: 'Teacher जोड़ने या bulk import में मदद चाहिए?' },
  classes: { label: 'Classes & Sections', tip: 'Classes/Sections setup में मदद चाहिए?' },
  attendance: { label: 'Attendance', tip: 'Attendance mark करने या reports में मदद चाहिए?' },
  fees: { label: 'Fees', tip: 'Fee structure या payment collection setup में मदद चाहिए?' },
  'fee-management': { label: 'Fees', tip: 'Fee structure या payment collection setup में मदद चाहिए?' },
  exams: { label: 'Exams & Results', tip: 'Exam या result entry में मदद चाहिए?' },
  timetable: { label: 'Timetable', tip: 'Timetable बनाने में मदद चाहिए?' },
  settings: { label: 'Settings & Integrations', tip: 'WhatsApp/Razorpay/SMTP setup में मदद चाहिए?' },
  'school-profile': { label: 'School Profile', tip: 'UDISE+ code से profile auto-fill करें — दिखाऊँ?' },
  'bulk-import': { label: 'Bulk Import', tip: 'CSV upload में कोई error? मैं मदद करता हूँ।' },
  'onboarding-schedule': { label: 'Onboarding & Data Upload', tip: 'Setup के अगले step में मदद चाहिए?' },
  communication: { label: 'Communication', tip: 'WhatsApp/SMS broadcast भेजने में मदद चाहिए?' },
  admissions: { label: 'Admissions', tip: 'Admission process setup में मदद चाहिए?' },
};
const getAayushPageContext = (pathname) => {
  const seg = (pathname || '').split('/').filter(Boolean)[0] || 'dashboard';
  return AAYUSH_PAGE_MAP[seg] || { label: seg.replace(/-/g, ' '), tip: 'इस page पर कोई मदद चाहिए? पूछिए!' };
};

function DashboardLayout({ children }) {
  const { user, token, handleLogout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  // Get active tab from URL path
  const getActiveTab = () => {
    const path = location.pathname.replace('/', '');
    return path || 'dashboard';
  };

  const setActiveTab = (tab) => {
    navigate(`/${tab}`);
  };

  return (
    <BreadcrumbProvider>
      <DemoModeProvider user={user} token={token}>
        <div className="min-h-screen bg-gray-100 flex flex-col">
          <Header user={user} onLogout={handleLogout} token={token} />
          <DemoModeBanner />
          <BackBar user={user} />
          <div className="flex flex-col md:flex-row flex-1">
            <div className="hidden md:block">
              <Sidebar role={user.role} activeTab={getActiveTab()} setActiveTab={setActiveTab} />
            </div>
            <div className="md:hidden p-4 bg-white shadow">
              <MobileMenu role={user.role} activeTab={getActiveTab()} setActiveTab={setActiveTab} />
            </div>
            <main className="flex-1 p-4 md:p-6 min-w-0 max-w-full overflow-x-hidden">{children}</main>
          </div>
          <LegalFooterWithLinks />
          <DemoFloatingActivate />
          <DemoActivateModal />
          <AccountSuspendedGate />
          <WelcomeTour user={user} token={token} />
          <SchoolNavTour user={user} />
          <DikshaGuidedDemo user={user} token={token} />
          {['platform_admin', 'school_admin', 'incharge'].includes(user.role) && (
            <LiveChat dockOffset loggedIn pageContext={getAayushPageContext(location.pathname)} />
          )}
          <LanguagePromptModal user={user} token={token} />
        </div>
      </DemoModeProvider>
    </BreadcrumbProvider>
  );
}

// Legal Footer - Minimal Copyright Only (for logged-in users)
// Note: When school's own domain is connected, this can render school's custom 4-column footer (P1)
function LegalFooterWithLinks() {
  return (
    <footer className="bg-gray-800 text-white py-4 mt-auto" data-testid="app-footer">
      <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-2 text-xs md:text-sm text-gray-400">
        <p>© 2025 System Of Success. All rights reserved.</p>
        <div className="flex gap-4">
          <Link to="/privacy" className="hover:text-white transition">Privacy</Link>
          <Link to="/terms" className="hover:text-white transition">Terms</Link>
          <Link to="/refund" className="hover:text-white transition">Refund</Link>
        </div>
      </div>
    </footer>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Validate token by making a test API call.
  // Returns 'valid' | 'invalid' | 'error'. Only 'invalid' (401/403) means the
  // token is truly bad → logout. Transient 5xx/network errors return 'error' and
  // we KEEP the stored session (server busy / blip must NOT log the user out).
  const validateToken = async (savedToken) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${savedToken}` }
      });
      if (res.ok) return 'valid';
      if (res.status === 401 || res.status === 403) return 'invalid';
      return 'error';
    } catch (err) {
      console.error('Token validation failed (network/transient):', err);
      return 'error';
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const savedToken = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (savedToken && savedUser) {
        const parsed = JSON.parse(savedUser);
        // Safety timeout: if /api/auth/me hangs, treat as transient 'error' (keep
        // session) so the app never gets stuck on the loading spinner.
        const result = await Promise.race([
          validateToken(savedToken),
          new Promise((r) => setTimeout(() => r('error'), 8000)),
        ]);
        if (result === 'invalid') {
          console.warn('Stored token is invalid (401/403), clearing session');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        } else {
          // 'valid' OR transient 'error' → keep the session (trust stored token).
          setToken(savedToken);
          setUser(parsed);
          // Pinterest Enhanced Match for already-logged-in users
          import('./utils/pinterestTag').then((m) =>
            m.setPinterestIdentity({ email: parsed?.email, phone: parsed?.phone })
          );
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const handleLogin = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    // Pinterest Enhanced Match — pass hashed email/phone on login
    import('./utils/pinterestTag').then((m) =>
      m.setPinterestIdentity({ email: userData?.email, phone: userData?.phone })
    );
    // Redirect to the page they were trying to access, or dashboard
    const from = location.state?.from?.pathname || '/dashboard';
    navigate(from, { replace: true });
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setToken(null);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, handleLogout }}>
      <SonnerToaster position="top-right" richColors />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          user ? <Navigate to="/dashboard" replace /> : <LandingPage onLoginClick={() => navigate('/login')} />
        } />
        
        <Route path="/login" element={
          user ? <Navigate to="/dashboard" replace /> : <LoginPage onLogin={handleLogin} />
        } />

        {/* First-time setup (forced password reset + school profile wizard) */}
        <Route path="/first-time-setup" element={
          user ? <FirstTimeSetupPage /> : <Navigate to="/login" replace />
        } />

        {/* Public School Match Page (for Parents) */}
        <Route path="/founding" element={<FoundingOfferPage />} />
        <Route path="/school-match" element={<SchoolMatchPage />} />
        <Route path="/find-school" element={<SchoolMatchPage />} />
        <Route path="/my-school" element={<MySchoolProfilePage />} />
        <Route path="/my-school-profile" element={<MySchoolProfilePage />} />
        <Route path="/data-sources" element={<DataSourcesPage />} />

        {/* Public Feature Pages (for Ads / Deep Linking - no login required) */}
        <Route path="/features" element={<PublicFeaturesListPage />} />
        <Route path="/features/:featureId" element={<FeatureMarketingPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsConditions />} />
        <Route path="/refund" element={<RefundPolicy />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/p" element={<PaymentRedirectPage />} />
        <Route path="/payment" element={<PaymentRedirectPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/pay-now" element={<PayNowStartPage />} />
        <Route path="/activate" element={<ActivatePage />} />
        <Route path="/book-demo" element={<BookDemoPage />} />

        {/* Public Payment Pages (accessed via WhatsApp links) */}
        <Route path="/receipt/:transactionId" element={<ReceiptPage />} />
        <Route path="/pay/:schoolId" element={<PayNowPage />} />
        <Route path="/admission-exam/:token" element={<PublicEntranceExam />} />
        <Route path="/admission-portal" element={<ParentAdmissionPortal />} />

        {/* Protected Dashboard Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardLayout>
              {user?.role === 'platform_admin' ? <PlatformDashboard token={token} /> : 
               user?.role === 'parent' || user?.role === 'student' ? <ParentPortalDashboard token={token} user={user} /> : 
               <SchoolDashboard token={token} />}
            </DashboardLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/schools" element={<ProtectedRoute><DashboardLayout><PlatformDashboard token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/classes" element={<ProtectedRoute><DashboardLayout><ClassesPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/students" element={<ProtectedRoute><DashboardLayout><StudentsPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/teachers" element={<ProtectedRoute><DashboardLayout><TeachersPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/attendance" element={<ProtectedRoute><DashboardLayout><AttendanceFullPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/exams" element={<ProtectedRoute><DashboardLayout><ExamsResultsPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/timetable" element={<ProtectedRoute><DashboardLayout><TimetableFullPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><DashboardLayout><CalendarPageFull token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/fees" element={<ProtectedRoute><DashboardLayout><FeesPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/communication" element={<ProtectedRoute><DashboardLayout><CommunicationPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/complaints" element={<ProtectedRoute><DashboardLayout><ComplaintsPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><DashboardLayout><ReportsPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/transport" element={<ProtectedRoute><DashboardLayout><TransportFullPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/hr-payroll" element={<ProtectedRoute><DashboardLayout><HRPayrollPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/library" element={<ProtectedRoute><DashboardLayout><LibraryPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/domain-settings" element={<ProtectedRoute><DashboardLayout><DomainSettingsPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/leave-management" element={<ProtectedRoute><DashboardLayout><LeaveManagementPage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/leave-apply" element={<ProtectedRoute><DashboardLayout><LeaveApplyPage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/emergency-exit" element={<ProtectedRoute><DashboardLayout><EmergencyExitPage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        
        {/* AI Features Routes */}
        <Route path="/ai" element={<ProtectedRoute><DashboardLayout><AIFeaturesPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/ai-features" element={<ProtectedRoute><DashboardLayout><AIFeaturesPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/ai-usage" element={<ProtectedRoute><DashboardLayout><TodayAIUsagePage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/wallet" element={<ProtectedRoute><DashboardLayout><WalletPage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/whatsapp" element={<ProtectedRoute><DashboardLayout><WhatsAppPage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        
        {/* Admin & Platform Routes */}
        <Route path="/bulk" element={<ProtectedRoute><DashboardLayout><BulkImportPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/bulk-import" element={<ProtectedRoute><DashboardLayout><BulkImportPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/data-tasks" element={<ProtectedRoute><DashboardLayout><DataTasksPage token={token} user={user} language={language} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/school-finder" element={<ProtectedRoute><DashboardLayout><SchoolFinderPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><DashboardLayout><AnalyticsDashboard token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/follow-ups" element={<ProtectedRoute><DashboardLayout><FollowUpsPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/admission-engine" element={<ProtectedRoute><DashboardLayout><AdmissionEngineDashboard token={token} schoolId={user?.school_id} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/padhai-emi" element={<ProtectedRoute><DashboardLayout><PadhaiEMIPage token={token} schoolId={user?.school_id} userRole={user?.role} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/fee-structure" element={<ProtectedRoute><DashboardLayout><FeeStructurePage token={token} schoolId={user?.school_id} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/campaign-manager" element={<ProtectedRoute><DashboardLayout><CampaignManager token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/nbfc-approval" element={<ProtectedRoute><DashboardLayout><NBFCApprovalManager token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/leave-requests" element={<ProtectedRoute><DashboardLayout><LeaveRequestsPageFull token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/setup-guide" element={<ProtectedRoute><DashboardLayout><SetupGuidePage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/school-analytics" element={<ProtectedRoute><DashboardLayout><SchoolAnalyticsPage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/school-profile" element={<ProtectedRoute><DashboardLayout>{user?.school_id ? <SchoolProfile360 schoolId={user.school_id} token={token} /> : <div className="p-6 text-slate-500">कोई school लिंक नहीं मिला।</div>}</DashboardLayout></ProtectedRoute>} />
        <Route path="/onboarding-schedule" element={<ProtectedRoute loginRedirect><DashboardLayout><OnboardingScheduler token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><DashboardLayout><BillingMyPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/diksha/chat/:sessionId" element={<DikshaChat />} />
        <Route path="/chat/:token" element={<ChatResumePage />} />
        <Route path="/aayush-rulebook" element={<ProtectedRoute><DashboardLayout><AayushRulebookEditor token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/aayush-knowledge" element={<ProtectedRoute><DashboardLayout><AayushKnowledgeBase token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/lead-crm" element={<ProtectedRoute><DashboardLayout><LeadCRM token={token} language={language} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/feature-editor" element={<ProtectedRoute><DashboardLayout><FeatureEditor token={token} language={language} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/udise" element={<ProtectedRoute><DashboardLayout><UDISEAffiliationPage token={token} language={language} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/social-media" element={<ProtectedRoute><DashboardLayout><SocialMediaPage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/newsletter" element={<ProtectedRoute><DashboardLayout><NewsletterManager token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><DashboardLayout><SettingsPage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><DashboardLayout><NotificationCenter token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/audit-logs" element={<ProtectedRoute><DashboardLayout><AuditLogsPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/doubt-history" element={<ProtectedRoute><DashboardLayout><DoubtHistoryPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/scheduler" element={<ProtectedRoute><DashboardLayout><SchedulerPage token={token} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/documents" element={<ProtectedRoute><DashboardLayout><DocumentsPage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/documents/competition" element={<ProtectedRoute><DashboardLayout><CompetitionComparisonPage user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/data-export" element={<ProtectedRoute><DashboardLayout><DataExportPage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/lead-nurturing" element={<ProtectedRoute><DashboardLayout><LeadNurturingPage token={token} /></DashboardLayout></ProtectedRoute>} />
        
        {/* Parent Portal Routes */}
        <Route path="/parent-dashboard" element={<ProtectedRoute><DashboardLayout><ParentDashboardPage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/portal-dashboard" element={<ProtectedRoute><DashboardLayout><ParentPortalDashboard token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/portal-attendance" element={<ProtectedRoute><DashboardLayout><PortalAttendancePage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/portal-fees" element={<ProtectedRoute><DashboardLayout><PortalFeesPage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/portal-announcements" element={<ProtectedRoute><DashboardLayout><PortalAnnouncementsPage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/portal-complaints" element={<ProtectedRoute><DashboardLayout><PortalComplaintsPage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        <Route path="/portal-bus-tracking" element={<ProtectedRoute><DashboardLayout><PortalBusTrackingPage token={token} user={user} /></DashboardLayout></ProtectedRoute>} />
        
        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthContext.Provider>
  );
}

// Mobile Menu Component
function MobileMenu({ role, activeTab, setActiveTab }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const menuItems = {
    platform_admin: ['dashboard', 'schools', 'lead-crm', 'feature-editor', 'data-tasks', 'whatsapp', 'ai'],
    school_admin: ['dashboard', 'classes', 'students', 'teachers', 'attendance', 'exams', 'timetable', 'calendar', 'fees', 'communication', 'complaints', 'social-media', 'notifications', 'newsletter', 'reports', 'bulk', 'ai', 'settings'],
    teacher: ['dashboard', 'students', 'attendance', 'exams', 'timetable', 'leave-requests', 'ai'],
    parent: ['portal-dashboard', 'portal-attendance', 'portal-fees', 'portal-announcements', 'portal-complaints', 'portal-bus-tracking'],
    student: ['portal-dashboard', 'portal-attendance', 'portal-fees', 'portal-announcements', 'portal-bus-tracking'],
  };
  
  const items = menuItems[role] || menuItems.school_admin;
  const currentLabel = activeTab.replace('portal-', '').replace(/-/g, ' ');
  
  return (
    <div className="relative z-50">
      <button 
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }} 
        className="flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg w-full justify-between"
        data-testid="mobile-menu-toggle"
      >
        <span className="capitalize font-medium">{currentLabel}</span>
        <svg className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 right-0 bg-white shadow-lg rounded-lg mt-2 z-50 max-h-80 overflow-y-auto border">
            {items.map(id => (
              <button 
                key={id} 
                onClick={() => { setActiveTab(id); setIsOpen(false); }}
                className={`w-full px-4 py-3 text-left capitalize border-b last:border-b-0 ${activeTab === id ? 'bg-orange-50 text-orange-700 font-medium' : 'hover:bg-gray-50'}`}
                data-testid={`mobile-nav-${id}`}
              >
                {id.replace('portal-', '').replace(/-/g, ' ')}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// WhatsApp Page

// Reports Page

// Bulk Import Page

// Leave Apply Page (Parent View)

// Emergency Exit Page

// UDISE+ Affiliation Page
function UDISEPage({ token }) {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('basic');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  
  useEffect(() => {
    loadProfile();
  }, []);
  
  const loadProfile = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/udise/complete-profile', token);
      setProfile(res);
      if (res.profile?.basic) setFormData(prev => ({...prev, basic: res.profile.basic}));
      if (res.profile?.affiliation) setFormData(prev => ({...prev, affiliation: res.profile.affiliation}));
      if (res.profile?.contact) setFormData(prev => ({...prev, contact: res.profile.contact}));
      if (res.profile?.medium_language) setFormData(prev => ({...prev, medium: res.profile.medium_language}));
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };
  
  const tabs = [
    { id: 'basic', label: language === 'hi' ? 'बेसिक प्रोफाइल' : 'Basic Profile', icon: '🏫' },
    { id: 'affiliation', label: language === 'hi' ? 'मान्यता विवरण' : 'Affiliation', icon: '📜' },
    { id: 'contact', label: language === 'hi' ? 'संपर्क' : 'Contact', icon: '📞' },
    { id: 'medium', label: language === 'hi' ? 'माध्यम/भाषा' : 'Medium/Language', icon: '🗣️' },
  ];
  
  if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full"></div></div>;
  
  return (
    <div className="space-y-6" data-testid="udise-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">🏛️ UDISE+ / {language === 'hi' ? 'मान्यता विवरण' : 'Affiliation Details'}</h1>
        <span className={`px-3 py-1 rounded-full text-sm ${(profile?.completion?.percentage || 0) === 100 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
          {profile?.completion?.percentage || 0}% Complete
        </span>
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg whitespace-nowrap flex items-center gap-2 transition ${
              activeTab === tab.id ? 'bg-orange-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
            {profile?.completion?.sections?.[tab.id === 'basic' ? 'basic_profile' : tab.id === 'medium' ? 'medium_language' : tab.id === 'affiliation' ? 'affiliation_details' : 'contact_information'] ? 
              <span className="text-green-400">✓</span> : <span className="text-yellow-400">○</span>}
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        {activeTab === 'basic' && (
          <div className="space-y-4">
            <h3 className="font-bold text-lg mb-4">{language === 'hi' ? 'बेसिक स्कूल प्रोफाइल' : 'Basic School Profile'}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">UDISE Code (11 digits)</label>
                <input className="w-full px-4 py-2 border rounded-lg" placeholder="e.g., 09320101001" value={formData.basic?.udise_code || ''} onChange={e => setFormData({...formData, basic: {...formData.basic, udise_code: e.target.value}})} />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">{language === 'hi' ? 'स्कूल का नाम' : 'School Name'}</label>
                <input className="w-full px-4 py-2 border rounded-lg" value={formData.basic?.school_name || ''} onChange={e => setFormData({...formData, basic: {...formData.basic, school_name: e.target.value}})} />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">{language === 'hi' ? 'स्कूल श्रेणी' : 'School Category'}</label>
                <select className="w-full px-4 py-2 border rounded-lg" value={formData.basic?.school_category || ''} onChange={e => setFormData({...formData, basic: {...formData.basic, school_category: e.target.value}})}>
                  <option value="">Select</option>
                  <option value="primary">Primary (1-5)</option>
                  <option value="upper_primary">Upper Primary (6-8)</option>
                  <option value="secondary">Secondary (9-10)</option>
                  <option value="higher_secondary">Higher Secondary (11-12)</option>
                  <option value="primary_to_higher_secondary">Primary to Higher Secondary (1-12)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">{language === 'hi' ? 'प्रबंधन' : 'Management'}</label>
                <select className="w-full px-4 py-2 border rounded-lg" value={formData.basic?.school_management || ''} onChange={e => setFormData({...formData, basic: {...formData.basic, school_management: e.target.value}})}>
                  <option value="">Select</option>
                  <option value="government">Government</option>
                  <option value="private_aided">Private Aided</option>
                  <option value="private_unaided">Private Unaided</option>
                  <option value="central_govt">Central Govt (KVS, NVS, etc.)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">{language === 'hi' ? 'जिला' : 'District'}</label>
                <input className="w-full px-4 py-2 border rounded-lg" value={formData.basic?.district || ''} onChange={e => setFormData({...formData, basic: {...formData.basic, district: e.target.value}})} />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">{language === 'hi' ? 'राज्य' : 'State'}</label>
                <input className="w-full px-4 py-2 border rounded-lg" value={formData.basic?.state || ''} onChange={e => setFormData({...formData, basic: {...formData.basic, state: e.target.value}})} />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Pin Code</label>
                <input className="w-full px-4 py-2 border rounded-lg" value={formData.basic?.pin_code || ''} onChange={e => setFormData({...formData, basic: {...formData.basic, pin_code: e.target.value}})} />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">{language === 'hi' ? 'स्थापना वर्ष' : 'Year of Establishment'}</label>
                <input type="number" className="w-full px-4 py-2 border rounded-lg" value={formData.basic?.year_of_establishment || ''} onChange={e => setFormData({...formData, basic: {...formData.basic, year_of_establishment: parseInt(e.target.value)}})} />
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'affiliation' && (
          <div className="space-y-4">
            <h3 className="font-bold text-lg mb-4">{language === 'hi' ? 'मान्यता विवरण' : 'Affiliation Details'}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">{language === 'hi' ? 'Secondary Board' : 'Secondary Board'}</label>
                <select className="w-full px-4 py-2 border rounded-lg" value={formData.affiliation?.affiliation_board_secondary || ''} onChange={e => setFormData({...formData, affiliation: {...formData.affiliation, affiliation_board_secondary: e.target.value}})}>
                  <option value="">Select</option>
                  <option value="cbse">CBSE</option>
                  <option value="icse">ICSE</option>
                  <option value="state">State Board</option>
                  <option value="nios">NIOS</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">{language === 'hi' ? 'Affiliation Number' : 'Affiliation Number'}</label>
                <input className="w-full px-4 py-2 border rounded-lg" value={formData.affiliation?.affiliation_number_secondary || ''} onChange={e => setFormData({...formData, affiliation: {...formData.affiliation, affiliation_number_secondary: e.target.value}})} />
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.affiliation?.is_minority_school || false} onChange={e => setFormData({...formData, affiliation: {...formData.affiliation, is_minority_school: e.target.checked}})} />
                  <span>{language === 'hi' ? 'अल्पसंख्यक विद्यालय' : 'Minority School'}</span>
                </label>
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.affiliation?.is_pm_shri_school || false} onChange={e => setFormData({...formData, affiliation: {...formData.affiliation, is_pm_shri_school: e.target.checked}})} />
                  <span>PM-SHRI School</span>
                </label>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'contact' && (
          <div className="space-y-4">
            <h3 className="font-bold text-lg mb-4">{language === 'hi' ? 'संपर्क जानकारी' : 'Contact Information'}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">{language === 'hi' ? 'प्रधानाचार्य का नाम' : 'Principal Name'}</label>
                <input className="w-full px-4 py-2 border rounded-lg" value={formData.contact?.head_name || ''} onChange={e => setFormData({...formData, contact: {...formData.contact, head_name: e.target.value}})} />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">{language === 'hi' ? 'मोबाइल' : 'Mobile'}</label>
                <input className="w-full px-4 py-2 border rounded-lg" value={formData.contact?.head_mobile || ''} onChange={e => setFormData({...formData, contact: {...formData.contact, head_mobile: e.target.value}})} />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">{language === 'hi' ? 'स्कूल ईमेल' : 'School Email'}</label>
                <input className="w-full px-4 py-2 border rounded-lg" value={formData.contact?.school_email || ''} onChange={e => setFormData({...formData, contact: {...formData.contact, school_email: e.target.value}})} />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">{language === 'hi' ? 'स्कूल फोन' : 'School Phone'}</label>
                <input className="w-full px-4 py-2 border rounded-lg" value={formData.contact?.school_phone || ''} onChange={e => setFormData({...formData, contact: {...formData.contact, school_phone: e.target.value}})} />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-gray-500 mb-1">{language === 'hi' ? 'वेबसाइट' : 'Website'}</label>
                <input className="w-full px-4 py-2 border rounded-lg" value={formData.contact?.school_website || ''} onChange={e => setFormData({...formData, contact: {...formData.contact, school_website: e.target.value}})} />
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'medium' && (
          <div className="space-y-4">
            <h3 className="font-bold text-lg mb-4">{language === 'hi' ? 'शिक्षा का माध्यम और भाषाएं' : 'Medium of Instruction & Languages'}</h3>
            <p className="text-sm text-gray-500 mb-4">{language === 'hi' ? 'विभिन्न स्तरों पर शिक्षा का माध्यम और पढ़ाई जाने वाली भाषाएं चुनें।' : 'Select medium of instruction and languages taught at different levels.'}</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">{language === 'hi' ? 'Primary माध्यम' : 'Primary Medium'}</label>
                <select className="w-full px-4 py-2 border rounded-lg" value={formData.medium?.medium_primary?.[0] || ''} onChange={e => setFormData({...formData, medium: {...formData.medium, medium_primary: [e.target.value]}})}>
                  <option value="">Select</option>
                  <option value="Hindi">Hindi</option>
                  <option value="English">English</option>
                  <option value="Both">Hindi + English</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">{language === 'hi' ? 'Secondary माध्यम' : 'Secondary Medium'}</label>
                <select className="w-full px-4 py-2 border rounded-lg" value={formData.medium?.medium_secondary?.[0] || ''} onChange={e => setFormData({...formData, medium: {...formData.medium, medium_secondary: [e.target.value]}})}>
                  <option value="">Select</option>
                  <option value="Hindi">Hindi</option>
                  <option value="English">English</option>
                  <option value="Both">Hindi + English</option>
                </select>
              </div>
            </div>
          </div>
        )}
        
        <div className="mt-6 pt-4 border-t flex justify-end">
          <button className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
            {language === 'hi' ? 'सहेजें' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Wrap App with LanguageProvider
function AppWithLanguage() {
  return (
    <LanguageProvider>
      <App />
    </LanguageProvider>
  );
}

export default AppWithLanguage;
